import type { DictationAdapter } from '@assistant-ui/core';
import { useDictationStore } from '~/stores/dictation.store';

/** Keeps uploads small; at typical Opus/AAC bitrates two minutes is well under 2 MB. */
export const MAX_RECORDING_MS = 2 * 60 * 1000;

// Chrome and Firefox record WebM/Opus; Safari only records MP4/AAC.
const PREFERRED_MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

export function isDictationSupported(): boolean {
	// mediaDevices is undefined outside secure contexts.
	return typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

export function microphoneErrorMessage(error: unknown): string {
	const name = error instanceof Error || error instanceof DOMException ? error.name : undefined;
	switch (name) {
		case 'NotAllowedError':
		case 'SecurityError':
			return "Microphone access is blocked. Allow it in your browser's site settings, then try again.";
		case 'NotFoundError':
		case 'OverconstrainedError':
			return 'No microphone was found. Connect one and try again.';
		case 'NotReadableError':
		case 'AbortError':
			return "Your microphone couldn't be started. It may be in use by another app.";
		default:
			return "Couldn't start recording.";
	}
}

export class TranscriptionError extends Error {
	constructor(
		message: string,
		readonly retryable: boolean
	) {
		super(message);
		this.name = 'TranscriptionError';
	}
}

export function transcriptionErrorForStatus(status: number): TranscriptionError {
	switch (status) {
		case 400:
			return new TranscriptionError("Your recording was empty or couldn't be read. Try again.", false);
		case 403:
			return new TranscriptionError('Voice input is not enabled for your business.', false);
		case 413:
			return new TranscriptionError('That recording was too long to transcribe. Try a shorter message.', false);
		case 415:
			return new TranscriptionError("Your browser recorded audio in a format we can't transcribe.", false);
		case 503:
			return new TranscriptionError('Voice input is temporarily unavailable. Try again in a few minutes.', true);
		default:
			return new TranscriptionError("Couldn't transcribe your recording.", true);
	}
}

export async function transcribeRecording(recording: Blob): Promise<string> {
	const body = new FormData();
	body.append('audio', recording, 'recording');

	let response: Response;
	try {
		response = await fetch('/api/chat/transcribe', { body, method: 'POST' });
	} catch {
		throw new TranscriptionError("Couldn't reach Glazier. Check your connection and retry.", true);
	}
	if (!response.ok) {
		// Forwarded to Datadog; browser recording quirks only show up in what the browser actually produced.
		console.error('[voice] Transcription failed', { size: recording.size, status: response.status, type: recording.type });
		throw transcriptionErrorForStatus(response.status);
	}

	const { transcript } = (await response.json()) as { transcript: string };
	return transcript;
}

type Listener<T> = (value: T) => void;

/**
 * Records with MediaRecorder and transcribes the whole clip on stop via /api/chat/transcribe. There are no interim
 * results: the composer receives one final transcript, which it appends to whatever the user had already typed.
 */
export class OpenAiDictationAdapter implements DictationAdapter {
	disableInputDuringDictation = true;

	listen(): DictationAdapter.Session {
		const { fail, setPhase } = useDictationStore.getState();
		const speechStartListeners = new Set<Listener<void>>();
		const speechEndListeners = new Set<Listener<DictationAdapter.Result>>();
		const speechListeners = new Set<Listener<DictationAdapter.Result>>();

		let recorder: MediaRecorder | undefined;
		let stream: MediaStream | undefined;
		let autoStopTimer: ReturnType<typeof setTimeout> | undefined;
		let stopPromise: Promise<void> | undefined;
		const chunks: Blob[] = [];

		// The composer polls `status` and tears the session down once it reads "ended".
		const session: DictationAdapter.Session = {
			cancel: () => {
				if (session.status.type === 'ended') return;
				end('cancelled');
				releaseMicrophone();
			},
			onSpeech: (callback) => subscribe(speechListeners, callback),
			onSpeechEnd: (callback) => subscribe(speechEndListeners, callback),
			onSpeechStart: (callback) => subscribe(speechStartListeners, callback as Listener<void>),
			status: { type: 'starting' },
			stop: () => {
				stopPromise ??= finish();
				return stopPromise;
			},
		};

		const end = (reason: 'cancelled' | 'error' | 'stopped') => {
			clearTimeout(autoStopTimer);
			session.status = { reason, type: 'ended' };
			if (reason !== 'error') setPhase({ type: 'idle' });
		};

		// A function rather than an inline check, so TypeScript doesn't keep the narrowing from before an await.
		const wasCancelled = () => session.status.type === 'ended';

		const releaseMicrophone = () => {
			if (recorder?.state === 'recording') {
				recorder.ondataavailable = null;
				recorder.stop();
			}
			stream?.getTracks().forEach((track) => track.stop());
		};

		const stopRecording = () =>
			new Promise<Blob>((resolve) => {
				if (!recorder || recorder.state === 'inactive') {
					resolve(new Blob(chunks, { type: recorder?.mimeType }));
					return;
				}
				const { mimeType } = recorder;
				// Deferred a task in case the final dataavailable lands after stop, as some Safari versions have done.
				recorder.onstop = () => setTimeout(() => resolve(new Blob(chunks, { type: mimeType })), 0);
				recorder.stop();
			});

		async function finish() {
			// Stopped while the permission prompt was still open: nothing was recorded.
			if (session.status.type === 'starting') {
				session.cancel();
				return;
			}
			if (session.status.type === 'ended') return;

			clearTimeout(autoStopTimer);
			const recording = await stopRecording();
			releaseMicrophone();
			if (recording.size === 0) {
				console.error('[voice] Recording was empty', { type: recording.type });
				fail('Nothing was recorded. Check that your microphone is working, then try again.');
				end('error');
				return;
			}
			setPhase({ type: 'transcribing' });

			try {
				const transcript = await transcribeRecording(recording);
				// Cancelled mid-upload (e.g. the message was sent): the store may already belong to a newer session.
				if (wasCancelled()) return;
				if (!transcript) {
					fail("Didn't catch that. Try again, a little closer to the microphone.");
					end('error');
					return;
				}
				speechListeners.forEach((listener) => listener({ isFinal: true, transcript }));
				speechEndListeners.forEach((listener) => listener({ isFinal: true, transcript }));
				end('stopped');
			} catch (error) {
				if (wasCancelled()) return;
				const retryable = !(error instanceof TranscriptionError) || error.retryable;
				fail(error instanceof Error ? error.message : "Couldn't transcribe your recording.", retryable ? recording : undefined);
				end('error');
			}
		}

		void (async () => {
			try {
				stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			} catch (error) {
				if (session.status.type === 'ended') return;
				fail(microphoneErrorMessage(error));
				end('error');
				return;
			}
			// Cancelled while the permission prompt was open.
			if (session.status.type === 'ended') {
				releaseMicrophone();
				return;
			}

			// Permission can succeed and recording still fail, e.g. when the browser can't start its encoder.
			try {
				const mimeType = PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
				recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
				recorder.ondataavailable = (event) => {
					if (event.data.size > 0) chunks.push(event.data);
				};
				recorder.start();
			} catch (error) {
				console.error('[voice] Recorder failed to start', error);
				releaseMicrophone();
				fail("Couldn't start recording.");
				end('error');
				return;
			}

			session.status = { type: 'running' };
			setPhase({ startedAt: Date.now(), type: 'recording' });
			speechStartListeners.forEach((listener) => listener());
			autoStopTimer = setTimeout(() => void session.stop(), MAX_RECORDING_MS);
		})();

		return session;
	}
}

function subscribe<T>(listeners: Set<Listener<T>>, callback: Listener<T>) {
	listeners.add(callback);
	return () => {
		listeners.delete(callback);
	};
}
